import { Box, Grid, TextField, IconButton, InputAdornment, Stack, Typography } from "@mui/material";
import { FormProvider } from "../../common/hook-form";
import { useFieldArray, useForm } from "react-hook-form";
import { apiConstants, post, get } from "../../common/constants";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import {
  T, cardSx, primaryBtnSx, fieldSx, FieldLabel, SectionCard, EmptyState,
} from "./webhookUi";

function EasyecomWebhook() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    defaultValues: {
      merchant: "",
      list: [{ channel: "", webhookConfig: { username: "", password: "", tokenURL: "", tokenType: "", easyEcomXApiKey: "" } }],
    },
  });
  const { handleSubmit, control, register, setValue, reset, watch } = methods;
  const merchantId = watch('merchant');
  const { fields, append } = useFieldArray({ control, name: "list" });

  const onSubmit = (data) => {
    setIsSubmitting(true);
    data?.list?.forEach((item) => { item.webhookConfig.authType = 'BASIC'; item.channel = 'Easyecom'; });
    const request = { merchantId, channelWebhookConfig: data.list };
    post(apiConstants.EASYECOM_WEBHOOK, request).then((res) => {
      if (res.data.status === "SUCCESS") {
        enqueueSnackbar("Easyecom credentials saved", { variant: 'success' });
      } else {
        enqueueSnackbar(res.data?.message || "Could not save credentials", { variant: 'error' });
      }
    }).catch((err) => {
      console.log(err);
      enqueueSnackbar(err?.response?.data?.message || "Could not save credentials", { variant: 'error' });
    }).finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    if (!merchantId) return;
    get(`${apiConstants.CHANNEL_WEBHOOK_SEARCH}?merchantId=${merchantId}`)
      .then((res) => {
        const rows = res?.data?.responseObj || [];
        reset({ merchant: merchantId, list: [{ channel: "", webhookConfig: { username: "", password: "", tokenURL: "", tokenType: "", easyEcomXApiKey: "" } }] });
        rows.forEach((item, index) => {
          if (index === 0) {
            setValue('list.0.channel', item.channel);
            setValue('list.0.webhookConfig.username', item.webhookConfig?.username || '');
            setValue('list.0.webhookConfig.password', item.webhookConfig?.password || '');
            setValue('list.0.webhookConfig.easyEcomXApiKey', item.webhookConfig?.easyEcomXApiKey || '');
          } else {
            append(item);
          }
        });
      })
      .catch((error) => console.error('Error fetching data:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  return (
    <Box>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <SectionCard
          title="Easyecom Credentials"
          subtitle="Publish order updates for shipments coming via the Easyecom integration."
          icon={<Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: T.AMBER_BG, display: 'grid', placeItems: 'center' }}><StorefrontOutlinedIcon sx={{ color: T.AMBER, fontSize: 22 }} /></Box>}
          sx={{ mb: 2 }}
        >
          <FieldLabel required>Merchant</FieldLabel>
          <Box sx={{ maxWidth: 460, ...fieldSx }}>
            <MerchantFilterCached fullWidth placeholder="Select a merchant…" />
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
            <StorefrontOutlinedIcon sx={{ fontSize: 15, color: T.MUTED }} />
            <Typography sx={{ fontSize: 12, color: T.MUTED }}>Pick a merchant to manage its Easyecom credentials.</Typography>
          </Stack>
        </SectionCard>

        {!merchantId ? (
          <EmptyState
            icon={<Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: T.AMBER_BG, display: 'grid', placeItems: 'center', mx: 'auto' }}><KeyOutlinedIcon sx={{ fontSize: 30, color: T.AMBER }} /></Box>}
            title="No merchant selected"
            subtitle="Choose a merchant above to configure its Easyecom username, password & API key."
          />
        ) : (
          <>
            <Stack spacing={2}>
              {fields.map((field, index) => (
                <SectionCard
                  key={field.id}
                  icon={<Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: '#fff', border: `1px solid ${T.BORDER}`, display: 'grid', placeItems: 'center' }}><KeyOutlinedIcon sx={{ fontSize: 17, color: T.AMBER }} /></Box>}
                  title="Easyecom Authentication"
                  subtitle="Basic auth credentials used to push updates"
                >
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Username</FieldLabel>
                      <TextField fullWidth size="small" autoComplete="off" placeholder="Easyecom username" sx={fieldSx} {...register(`list.${index}.webhookConfig.username`)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Password</FieldLabel>
                      <TextField
                        fullWidth size="small" autoComplete="new-password" placeholder="Easyecom password"
                        type={showPassword ? "text" : "password"} sx={fieldSx}
                        {...register(`list.${index}.webhookConfig.password`)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowPassword((s) => !s)} edge="end">
                                {showPassword ? <VisibilityOutlinedIcon sx={{ fontSize: 19 }} /> : <VisibilityOffOutlinedIcon sx={{ fontSize: 19 }} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FieldLabel>Easyecom X-API Key</FieldLabel>
                      <TextField fullWidth size="small" placeholder="X-API key" sx={fieldSx} {...register(`list.${index}.webhookConfig.easyEcomXApiKey`)} />
                    </Grid>
                  </Grid>
                </SectionCard>
              ))}
            </Stack>

            <Box sx={{ ...cardSx, position: 'sticky', bottom: 12, mt: 2, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', boxShadow: '0 -2px 12px rgba(16,24,40,0.08)' }}>
              <LoadingButton loading={isSubmitting} variant="contained" type="submit" sx={primaryBtnSx}>Save credentials</LoadingButton>
            </Box>
          </>
        )}
      </FormProvider>
    </Box>
  );
}

export default EasyecomWebhook;
